import { startGetCurrencyTax } from "../../../actions/adsCampaigns";

// Helper: clamp to [min, max]
const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));

// Helper function to extract numeric value from strings formatted like "amount currency"
export function extractNumericValue(formattedValue) {
  if (!formattedValue) return 0;

  if (typeof formattedValue === "string") {
    // Take only the first value (first line)
    const firstLine = formattedValue.split("\n")[0];
    const match = firstLine.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  return 0;
}

/**
 * ENGAGEMENT (30%) - CLEAR WEIGHTS (MORE FLEXIBLE):
 * - Logins: 50 points (50%)
 * - Days since last login: 50 points (50%)
 * Total = 100 points (not normalized, direct sum)
 */
function calculateEngagementScore(logins, daysSinceLogin) {
  let loginPts = 0;
  if (logins >= 10) loginPts = 50;
  else if (logins >= 5) loginPts = 43.75;
  else if (logins >= 2) loginPts = 31.25;
  else if (logins >= 1) loginPts = 18.75;

  let recencyPts = 0;
  if (daysSinceLogin === null) recencyPts = 0;
  else if (daysSinceLogin <= 7) recencyPts = 50;
  else if (daysSinceLogin <= 14) recencyPts = 41.67;
  else if (daysSinceLogin <= 21) recencyPts = 33.33;
  else if (daysSinceLogin <= 30) recencyPts = 25;

  return clamp(loginPts + recencyPts);
}
/**
 * PERFORMANCE (30%) - CLEAR WEIGHTS:
 * - Current ROAS: 60 points (60%)
 * - ROAS trend: 20 points (20%)
 * - CPA trend: 20 points (20%)
 * Total = 100 points (not normalized, direct sum)
 *
 * If NO historical data: ROAS trend = 20 and CPA trend = 20 (maximums)
 */
function calculatePerformanceScore(metrics = {}) {
  const { totals = {}, totalsLastPeriod = {} } = metrics;
  const { roas = 0, cpa = {} } = totals;
  const { roas: roasLastPeriod = 0, cpa: cpaLastPeriod = {} } =
    totalsLastPeriod;

  const MAX_ROAS_PTS = 60;
  let roasPts = 0;

  if (roas >= 4.0) roasPts = MAX_ROAS_PTS;
  else if (roas >= 3.0) roasPts = MAX_ROAS_PTS * 0.75;
  else if (roas >= 2.5) roasPts = MAX_ROAS_PTS * 0.5;
  else if (roas >= 2.0) roasPts = MAX_ROAS_PTS * 0.25;

  const cpaObject = typeof cpa === "object" && cpa !== null ? cpa : {};
  const cpaLastObject =
    typeof cpaLastPeriod === "object" && cpaLastPeriod !== null
      ? cpaLastPeriod
      : {};
  const cpaLastCurrencies = Object.keys(cpaLastObject);
  const hasHistoricalData = roasLastPeriod > 0 || cpaLastCurrencies.length > 0;
  let roasTrendPts = 0;

  if (hasHistoricalData && roasLastPeriod > 0) {
    const roasChange = (roas - roasLastPeriod) / (roasLastPeriod || 1);

    if (roasChange >= 0.15) roasTrendPts = 20;
    else if (roasChange >= 0.1) roasTrendPts = 15;
    else if (roasChange >= 0.05) roasTrendPts = 12;
    else if (roasChange > 0.0) roasTrendPts = 10;
    else if (roasChange <= -0.25) roasTrendPts = 0;
    else if (roasChange <= -0.1) roasTrendPts = 2;
    else if (roasChange < 0.0) roasTrendPts = 5;
  } else if (!hasHistoricalData) {
    roasTrendPts = 20;
  }
  const MAX_CPA_PTS = 20;
  let cpaTrendPts = 0;

  const cpaCurrencies = Object.keys(cpaObject);

  if (!hasHistoricalData) {
    cpaTrendPts = MAX_CPA_PTS;
  } else if (cpaLastCurrencies.length === 0 && cpaCurrencies.length > 0) {
    cpaTrendPts = MAX_CPA_PTS;
  } else if (cpaLastCurrencies.length > 0 && cpaCurrencies.length === 0) {
    cpaTrendPts = 0;
  } else if (cpaCurrencies.length > 0 && cpaLastCurrencies.length > 0) {
    let totalPoints = 0;
    let currencyCount = 0;

    for (const currency of cpaCurrencies) {
      const currentValue = cpaObject[currency];
      const previousValue = cpaLastObject[currency];

      if (currentValue > 0 && previousValue > 0) {
        const cpaChange = (currentValue - previousValue) / previousValue;
        let points = 0;

        if (cpaChange <= -0.15) points = 20;
        else if (cpaChange <= -0.1) points = 17.5;
        else if (cpaChange <= -0.05) points = 15;
        else if (cpaChange < 0.0) points = 12.5;
        else if (cpaChange >= 0.25) points = 0;
        else if (cpaChange >= 0.1) points = 5;
        else if (cpaChange > 0.0) points = 7.5;
        else points = 12;

        totalPoints += points;
        currencyCount++;
      } else if (currentValue > 0 && (!previousValue || previousValue === 0)) {
        totalPoints += MAX_CPA_PTS;
        currencyCount++;
      }
    }

    cpaTrendPts = currencyCount > 0 ? totalPoints / currencyCount : 0;
  }

  return clamp(roasPts + roasTrendPts + cpaTrendPts);
}

/**
 * ACTIVITY (30%) - DIRECT SUM:
 * - Spend vs MRR: 0-100 points
 * Total = 100 points (not normalized, direct sum)
 */
async function calculateActivityScore(
  spend,
  mrr,
  dispatch,
  currencyRatesCache = {}
) {
  let spendPts = 0;

  // Handle spend as object with multiple currencies (like CPA)
  const spendObject = typeof spend === "object" && spend !== null ? spend : {};
  const spendCurrencies = Object.keys(spendObject);

  // Sum all spend values across all currencies, converting to USD
  let totalSpend = 0;
  if (spendCurrencies.length > 0) {
    for (const currency of spendCurrencies) {
      const spendValue = spendObject[currency];
      let numericValue = 0;

      if (typeof spendValue === "number" && spendValue > 0) {
        numericValue = spendValue;
      } else if (typeof spendValue === "string") {
        numericValue = extractNumericValue(spendValue);
      }

      if (numericValue > 0) {
        // Get conversion rate to USD (default to 1 if USD or if conversion fails)
        let conversionRate = 1;

        if (currency !== "USD") {
          // Check cache first
          if (currencyRatesCache[currency] !== undefined) {
            conversionRate = currencyRatesCache[currency];
          } else if (dispatch) {
            // If not in cache, fetch and store in cache
            try {
              const response = await dispatch(startGetCurrencyTax(currency));
              if (response?.ok && response?.data) {
                conversionRate = response.data;
                // Store in cache for future use
                currencyRatesCache[currency] = conversionRate;
              }
            } catch (error) {
              console.warn(
                `Failed to get conversion rate for ${currency}, using 1.0`,
                error
              );
              conversionRate = 1;
              // Store default in cache to avoid repeated failed requests
              currencyRatesCache[currency] = 1;
            }
          }
        }

        const spendInUSD = numericValue * conversionRate;
        totalSpend += spendInUSD;
      }
    }
  }

  const ratio = mrr > 0 ? totalSpend / mrr : 0;

  if (ratio >= 2.0) spendPts = 100;
  else if (ratio >= 1.5) spendPts = 80;
  else if (ratio >= 1.0) spendPts = 60;
  else if (ratio >= 0.5) spendPts = 40;
  else if (ratio >= 0.1) spendPts = 20;

  return clamp(spendPts);
}

// FRICTION (10%)
function calculateFrictionScore(invoiceStatus) {
  if (invoiceStatus === "overdue") return 0;
  if (invoiceStatus === "pending") return 50;
  if (invoiceStatus === "paid") return 100;
  return 50;
}

// FINAL HEALTH SCORE (0-100)
/**
 * @returns {Promise<{ score: number, pillars: { engagement: number, performance: number, activity: number, friction: number } }>}
 */
export async function calculateHealthyScore(
  client,
  dispatch,
  currencyRatesCache = {}
) {
  const engagementScore = calculateEngagementScore(
    client.totalLoginsLast30Days,
    client.lastLogin
  );
  const frictionScore = calculateFrictionScore(client.invoiceStatus);

  const performanceScore = calculatePerformanceScore(client.metrics);

  const activityScore = await calculateActivityScore(
    client.metrics?.totals?.spend,
    client.mrr,
    dispatch,
    currencyRatesCache
  );

  const finalScore = Math.round(
    engagementScore * 0.3 +
      performanceScore * 0.3 +
      activityScore * 0.3 +
      frictionScore * 0.1
  );

  const score = clamp(finalScore);

  return {
    score,
    pillars: {
      engagement: engagementScore,
      performance: performanceScore,
      activity: activityScore,
      friction: frictionScore,
    },
  };
}

export function getHealthColor(score) {
  if (score >= 80) return "#22c55e";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
}
